// @ts-nocheck
// Side popup for incoming calls on the affiliate's PBX extension: who is calling
// (matched to their lead / referred customer) and the last AI call summary.
import React, {useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import moment from 'moment'
import {AFF} from './affiliateTheme'
import {getPbxLive} from '../core/_requests'

const OUTCOME = {interested: 'מעוניין', callback: 'לחזור אליו', not_interested: 'לא מעוניין', irrelevant: 'לא רלוונטי', no_conversation: 'לא הייתה שיחה'}

const IncomingCallPopup = () => {
  const navigate = useNavigate()
  const [call, setCall] = useState<any>(null) // last live payload (kept after hangup)
  const [ended, setEnded] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [, tick] = useState(0)
  const lastKey = useRef('')
  const endTimer = useRef<any>(null)
  const live = useRef(false) // is a call live right now (avoids stale state inside the poll loop)

  useEffect(() => {
    let stop = false
    let fails = 0
    const poll = async () => {
      if (stop) return
      try {
        const r = (await getPbxLive()).data
        fails = 0
        if (r.state) {
          const key = `${r.phone}|${r.since}`
          if (key !== lastKey.current) { lastKey.current = key; setHidden(false) }
          clearTimeout(endTimer.current)
          live.current = true
          setCall(r)
          setEnded(false)
        } else if (live.current) {
          // Just hung up: keep the card 30s so the details stay readable.
          live.current = false
          setEnded(true)
          endTimer.current = setTimeout(() => setCall(null), 30000)
        }
      } catch (_) {
        fails++
      }
      // Back off if the endpoint keeps failing (not an affiliate / PBX down).
      setTimeout(poll, fails > 3 ? 30000 : 2500)
    }
    poll()
    const t = setInterval(() => tick((x) => x + 1), 1000)
    return () => { stop = true; clearInterval(t); clearTimeout(endTimer.current) }
  }, [])

  if (!call || hidden) return null
  const who = call.lead?.name || call.customer?.name || (call.phone ? 'מספר לא מוכר' : 'מספר חסוי')
  const secs = call.since ? Math.max(0, Math.floor((Date.now() - call.since) / 1000)) : 0
  const stateLabel = ended ? 'השיחה הסתיימה' : call.state === 'ringing' ? '📞 מצלצל עכשיו בשלוחה ' + call.ext : `בשיחה · ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
  const color = ended ? AFF.muted : call.state === 'ringing' ? AFF.warning : AFF.success
  const lc = call.lastCall

  return (
    <div
      dir='rtl'
      style={{
        position: 'fixed', top: 90, insetInlineEnd: 20, zIndex: 1070, width: 340, maxWidth: 'calc(100vw - 32px)',
        background: '#fff', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
        borderTop: `6px solid ${color}`, padding: 16,
        animation: call.state === 'ringing' && !ended ? 'pbxPulse 1.2s ease-in-out infinite' : undefined,
      }}
    >
      <style>{'@keyframes pbxPulse{0%,100%{box-shadow:0 12px 40px rgba(0,0,0,.22)}50%{box-shadow:0 12px 40px rgba(255,170,0,.55)}}'}</style>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <div style={{fontSize: 13, fontWeight: 700, color}}>{stateLabel}</div>
        <button className='btn-close' style={{fontSize: 10}} onClick={() => setHidden(true)} />
      </div>
      <div style={{fontSize: 22, fontWeight: 800, color: AFF.ink, marginTop: 6}}>{who}</div>
      <div dir='ltr' style={{textAlign: 'right', color: AFF.accent, fontWeight: 600}}>{call.phone}</div>
      {(call.lead?.agency_name || call.customer?.company_name) && (
        <div style={{color: AFF.muted, fontSize: 13}}>{call.lead?.agency_name || call.customer?.company_name}</div>
      )}
      {call.customer && <span className='badge badge-light-success mt-2' style={{fontWeight: 700}}>לקוח שלך</span>}
      {call.lead?.last_note && <div style={{marginTop: 8, fontSize: 13}}><b>הערה אחרונה:</b> {call.lead.last_note}</div>}
      {call.lead?.follow_up_time && <div style={{fontSize: 13}}><b>מעקב:</b> {moment(call.lead.follow_up_time).format('DD/MM HH:mm')}</div>}
      {lc && (
        <div style={{marginTop: 10, background: '#f4f7ff', border: '1px solid #d9e2fb', borderRadius: 10, padding: 10, fontSize: 13}}>
          <div style={{color: AFF.muted, marginBottom: 4}}>
            🤖 שיחה קודמת · {moment(lc.at).fromNow()}{lc.agentName ? ` · ${lc.agentName}` : ''}
            {lc.ai?.outcome && <b style={{color: AFF.ink}}> · {OUTCOME[lc.ai.outcome] || lc.ai.outcome}</b>}
          </div>
          <div style={{color: AFF.ink}}>{lc.ai?.summary || 'אין סיכום לשיחה הקודמת'}</div>
          {lc.ai?.next_step && <div style={{marginTop: 4}}><b>הצעד הבא:</b> {lc.ai.next_step}</div>}
        </div>
      )}
      {!call.lead && !call.customer && (
        <div style={{marginTop: 8, color: AFF.muted, fontSize: 13}}>
          {call.phone ? 'המספר לא נמצא בלידים או בלקוחות שלך.' : 'המספר של המתקשר לא הועבר (חסוי), אי אפשר לזהות אותו.'}
        </div>
      )}
      <button
        className='btn btn-sm w-100 mt-3'
        style={{background: AFF.accent, color: '#fff', fontWeight: 700, borderRadius: 10}}
        disabled={!call.phone}
        onClick={() => navigate(`/affiliate/leads-manager?q=${encodeURIComponent(call.phone || '')}`)}
      >
        <i className='bi bi-person-lines-fill me-2' />
        פתח בלידים
      </button>
    </div>
  )
}

export default IncomingCallPopup
